import React, { createContext, useContext, useState, useEffect } from 'react';
import { saveRegistration, getRegistrationStatus, getAllRegistrations } from '../api/register';
import { formatEmailForDisplay } from '@/lib/utils';

const STORAGE_KEY = 'acos_registrations';
const TOTAL_SPOTS = 100;
export const DEADLINE = new Date('2025-02-12T23:59:59');

interface Registration {
  email: string;
  timestamp: number;
  name: string;
  company: string;
}

interface WaitlistContextType {
  spotsLeft: number;
  timeLeft: string;
  registerSpot: (email: string, name: string, company: string) => Promise<boolean>;
  isRegistered: (email: string) => boolean;
  resetRegistrations: () => void;
  lastRegistration: { name: string; company: string } | null;
  registrations: Registration[];
  refreshRegistrations: () => Promise<void>;
}

const WaitlistContext = createContext<WaitlistContextType | undefined>(undefined);

export const WaitlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [spotsLeft, setSpotsLeft] = useState(TOTAL_SPOTS);
  const [timeLeft, setTimeLeft] = useState('');
  const [lastRegistration, setLastRegistration] = useState<{ name: string; company: string } | null>(null);

  const refreshRegistrations = async () => {
    try {
      const allRegistrations = await getAllRegistrations();
      setRegistrations(allRegistrations);
      const status = await getRegistrationStatus();
      setSpotsLeft(status.spotsLeft);
    } catch (error) {
      console.error('Error refreshing registrations:', error);
    }
  };

  // Load initial registration data
  useEffect(() => {
    refreshRegistrations();
  }, []);

  // Update countdown timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const diff = DEADLINE.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeLeft('00:00:00:00');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const formattedDays = String(days).padStart(2, '0');
      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');

      setTimeLeft(`${formattedDays}:${formattedHours}:${formattedMinutes}:${formattedSeconds}`);
    };

    const timer = setInterval(updateTimer, 1000);
    updateTimer();
    return () => clearInterval(timer);
  }, []);

  // Function to check if an email is already registered
  const isRegistered = (email: string): boolean => {
    if (!email) return false;
    return registrations.some(reg => reg.email.toLowerCase() === email.toLowerCase());
  };

  // Function to register a spot
  const registerSpot = async (email: string, name: string, company: string): Promise<boolean> => {
    if (!email || !name || !company) return false;
    
    try {
      const success = await saveRegistration(email, name, company);
      
      if (success) {
        // Refresh the full registration list
        await refreshRegistrations();
        
        // Set last registration for notification
        setLastRegistration({ name, company });
        
        console.log('Registration successful. Spots left:', spotsLeft);
      }
      
      return success;
    } catch (error) {
      console.error('Error registering spot:', error);
      return false;
    }
  };

  // Reset function for debugging (Note: this won't affect the JSON file)
  const resetRegistrations = () => {
    setRegistrations([]);
    setSpotsLeft(TOTAL_SPOTS);
  };

  return (
    <WaitlistContext.Provider value={{ 
      spotsLeft,
      timeLeft, 
      registerSpot, 
      isRegistered,
      resetRegistrations,
      lastRegistration,
      registrations,
      refreshRegistrations
    }}>
      {children}
    </WaitlistContext.Provider>
  );
};

export const useWaitlist = () => {
  const context = useContext(WaitlistContext);
  if (context === undefined) {
    throw new Error('useWaitlist must be used within a WaitlistProvider');
  }
  return context;
}; 